/**
 * Liens officiels pré-remplis pour l'aide aux démarches.
 *
 * À partir du titre d'une fiche démarche, on déduit le lien officiel le plus
 * pertinent (ANTS, service-public.fr, etc.). Aucune saisie côté mairie : la
 * détection se fait par mots-clés. Renvoie null si aucun lien évident.
 *
 * Les libellés restent volontairement génériques et pointent vers des portails
 * nationaux stables (pas de deep-link fragile).
 */

export interface LienDemarche {
  url: string;
  label: string;
}

const REGLES: { motsCles: RegExp; lien: LienDemarche }[] = [
  {
    // Item 13.3 — perte ou vol d'un titre (CNI, passeport, permis, papiers).
    //
    // ⚠️ PLACÉE EN PREMIER, et ce n'est pas un détail : la détection retourne
    // au premier match, et « Vol de passeport » matcherait sinon la règle
    // « passeport » avec son label « Pré-demande en ligne » — exact quant à
    // l'URL, trompeur quant au libellé (il s'agit d'un RENOUVELLEMENT après
    // perte/vol, qui exige en plus un timbre fiscal de 25 €). Les deux sens
    // de phrase sont couverts (« perte de carte » / « carte perdue »).
    motsCles: /(\b(perte|perdu[es]?|vol|vol[ée]e?s?)\b[\s\S]{0,40}\b(carte|identit|passeport|papiers?|permis|titre)|\b(carte|cni|passeport|papiers?|permis|titre)[\s\S]{0,40}\b(perte|perdu[es]?|vol[ée]?e?s?)\b)/i,
    lien: { url: 'https://ants.gouv.fr/', label: 'Renouvellement après perte ou vol (ANTS)' },
  },
  {
    // Item 13.4 — l'achat du timbre fiscal électronique. Portail racine DGFiP,
    // vérifié le 02/08/2026 (service-public F21258, economie.gouv.fr) : le
    // timbre papier n'existe plus depuis 2019, tout passe par ce site ou les
    // buralistes agréés.
    motsCles: /\btimbre[s]?\s+fisca/i,
    lien: { url: 'https://timbres.impots.gouv.fr/', label: 'Acheter un timbre fiscal (impots.gouv.fr)' },
  },
  {
    // Item 13.4 — le paiement des amendes et forfaits de post-stationnement.
    // « Seul site gouvernemental de paiement en ligne des amendes » (vérifié
    // le 02/08/2026 sur amendes.gouv.fr même) — le préciser protège aussi
    // l'administré des sites frauduleux qui singent ce paiement.
    motsCles: /\b(amendes?|contraventions?|forfait\s+de\s+post-?stationnement|\bfps\b)\b/i,
    lien: { url: 'https://www.amendes.gouv.fr/', label: 'Payer en ligne (amendes.gouv.fr — site officiel)' },
  },
  {
    // Carte nationale d'identité → pré-demande ANTS
    motsCles: /\b(carte\s+nationale|cni|carte\s+d['’]identit)/i,
    lien: { url: 'https://ants.gouv.fr/', label: 'Pré-demande en ligne (ANTS)' },
  },
  {
    // Passeport → pré-demande ANTS
    motsCles: /\bpasseport/i,
    lien: { url: 'https://ants.gouv.fr/', label: 'Pré-demande en ligne (ANTS)' },
  },
  {
    // Permis de conduire / carte grise → ANTS
    motsCles: /\b(permis\s+de\s+conduire|carte\s+gris|certificat\s+d['’]immatriculation)/i,
    lien: { url: 'https://ants.gouv.fr/', label: 'Démarche en ligne (ANTS)' },
  },
  {
    // Actes d'état civil → service-public
    motsCles: /\bacte\s+(de\s+)?(naissance|mariage|d[ée]c[èe]s)/i,
    lien: { url: 'https://www.service-public.fr/particuliers/vosdroits/N359', label: 'Demande en ligne (service-public.fr)' },
  },
  {
    // Listes électorales → service-public
    motsCles: /\b(liste[s]?\s+[ée]lectoral|inscription\s+.*vote|s['’]inscrire.*[ée]lect)/i,
    lien: { url: 'https://www.service-public.fr/particuliers/vosdroits/R16396', label: 'Inscription en ligne (service-public.fr)' },
  },
  {
    // Recensement citoyen → service-public
    motsCles: /\brecensement\s+citoyen/i,
    lien: { url: 'https://www.service-public.fr/particuliers/vosdroits/R2054', label: 'Recensement en ligne (service-public.fr)' },
  },
  {
    // Journée défense et citoyenneté
    motsCles: /\b(journ[ée]e\s+d[ée]fense|jdc)/i,
    lien: { url: 'https://www.service-public.fr/particuliers/vosdroits/F871', label: 'Informations (service-public.fr)' },
  },
];

// Lien générique de repli : le portail national des démarches.
const LIEN_GENERIQUE: LienDemarche = {
  url: 'https://www.service-public.fr/',
  label: 'Portail des démarches (service-public.fr)',
};

/**
 * Retourne le lien officiel pour une démarche donnée (par son titre), ou null si
 * aucun mot-clé connu n'est reconnu. On ne renvoie PAS le lien générique ici pour
 * laisser l'appelant décider s'il veut un repli.
 */
export function lienOfficielDemarche(titre: string): LienDemarche | null {
  const t = titre ?? '';
  for (const r of REGLES) {
    if (r.motsCles.test(t)) return r.lien;
  }
  return null;
}

export { LIEN_GENERIQUE };
